import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CanEdit } from "@/shared/readOnlyManager";
import axios from "axios";
import { Table } from "reactstrap";
import { useTable, useSortBy } from "react-table";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";

import { getEvents } from "@/services/program/getEvents";
import { getEvent } from "@/services/program/getEvent";
import ModalWrapper from "../components/ModalWrapper";
import { useDispatch, flashError, flashSuccess } from "@/shared/components/flash";
import { Sorting } from "@/shared/apiTableHelper";
import RemoveHierarchyModal from "../components/RemoveHierarchyModal";
import DeletedResultHierarchyModal from "../components/DeletedResultHierarchyModal";
import RemoveSelectedHierarchyModal from "../components/RemoveSelectedHierarchyModal";

const Events = ({ program, organization, reload = 0 }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [trigger, setTrigger] = useState(0);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setOpen] = useState(false);
  const [modalName, setModalName] = useState(null);
  const [checkedState, setCheckedState] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [isDeleteHierarchy, setDeleteHierarchy] = useState(false);
  const [isDeleteHierarchyList, setDeleteHierarchyList] = useState(false);
  const [deleteHierarchyListData, setDeleteHierarchyListData] = useState([]);
  const [isDeleteHierarchyResult, setDeleteHierarchyResult] = useState(false);
  const [deleteHierarchyListDataResult, setDeleteHierarchyListDataResult] = useState(
    []
  );
  const [busyEventId, setBusyEventId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const loadEvents = useCallback(async () => {
    if (!organization?.id || !program?.id) {
      setEvents([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const items = await getEvents(organization.id, program.id, { disabled: true });
      setEvents(Array.isArray(items) ? items : []);
    } catch (error) {
      setEvents([]);
      setErrorMessage("Unable to load events for this program.");
    } finally {
      setLoading(false);
    }
  }, [organization?.id, program?.id]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, reload, trigger]);

  useEffect(() => {
    const timeoutId = setTimeout(() => ReactTooltip.rebuild(), 0);
    return () => clearTimeout(timeoutId);
  }, [events, loading]);

  const toggle = useCallback((name = null, shouldReload = false) => {
    if (name) {
      setModalName(name);
      setOpen(true);
      return;
    }

    if (shouldReload) {
      setTrigger((value) => value + 1);
    }

    setOpen(false);
  }, []);

  const toggleDeleteHierarchy = (eventId) => {
    setSelectedEventId(eventId || null);
    setDeleteHierarchy((prevState) => !prevState);
  };

  const toggleDeleteHierarchyList = () => {
    setDeleteHierarchyList((prevState) => !prevState);
  };

  const toggleDeleteHierarchyResult = () => {
    setDeleteHierarchyResult((prevState) => !prevState);
  };

  const onClickEditEvent = useCallback(
    async (eventId) => {
      try {
        setBusyEventId(eventId);
        const item = await getEvent(program.organization_id, program.id, eventId);
        setEvent(item);
        toggle("EditEvent");
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to load event.");
      } finally {
        setBusyEventId(null);
      }
    },
    [dispatch, program?.id, program?.organization_id, toggle]
  );

  const onDeleteEvent = useCallback(
    async (e, eventId) => {
      try {
        setBusyEventId(eventId);
        const response = await axios.delete(
          `/organization/${program.organization_id}/program/${program.id}/event/${eventId}`
        );

        if (response.status === 200) {
          flashSuccess(dispatch, "Event deleted successfully!");
          toggleDeleteHierarchy();
          setTrigger((value) => value + 1);
        }
      } catch (error) {
        flashError(dispatch, error?.response?.data || "Unable to delete event.");
      } finally {
        setBusyEventId(null);
      }
    },
    [dispatch, program?.id, program?.organization_id]
  );

  const onDeleteHierarchy = () => {
    axios
      .get(
        `/organization/${program.organization_id}/program/${program.id}/event/${selectedEventId}/delete-hierarchy-list?excludeArchived=true`
      )
      .then((res) => {
        if (res.status === 200) {
          setDeleteHierarchyListData(res.data);
          const nextCheckedState = res.data.map((item) => ({
            checked: true,
            value: item.event_id,
          }));
          setCheckedState(nextCheckedState);
          toggleDeleteHierarchyList();
          toggleDeleteHierarchy(selectedEventId);
        }
      })
      .catch((err) => {
        flashError(dispatch, err?.response?.data || err);
        setLoading(false);
        toggleDeleteHierarchyList();
      });
  };

  const onDeleteHierarchyList = () => {
    const eventIds = checkedState
      .filter((item) => item.checked)
      .map((item) => item.value);

    axios
      .delete(
        `/organization/${program.organization_id}/program/${program.id}/event/${selectedEventId}/delete-event-hierarchy`,
        {
          data: { event_ids: eventIds },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setDeleteHierarchyListDataResult(res.data.deleted_data);
          flashSuccess(dispatch, "Event(s) deleted successfully!");
          toggleDeleteHierarchyResult();
          toggleDeleteHierarchyList();
          setTrigger((value) => value + 1);
        }
      })
      .catch((err) => {
        flashError(dispatch, err?.response?.data || "Failed to delete events.");
        setLoading(false);
        toggleDeleteHierarchyResult();
      });
  };

  const columns = useMemo(
    () => [
      {
        Header: "Event",
        tooltip: "Name of the reward event",
        accessor: "name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <strong>{value || "Untitled event"}</strong>
          </div>
        ),
      },
      {
        Header: "Type",
        tooltip: "Category of reward event",
        accessor: "event_type.name",
        Cell: ({ value }) => (
          <div className="manager-program-settings__cell-stack">
            <span>{value || "No event type"}</span>
          </div>
        ),
      },
      {
        Header: "Status",
        tooltip: "Whether this event is currently active or disabled",
        accessor: "enable",
        Cell: ({ value }) => (
          <span
            className={`manager-program-settings__status-badge${
              value ? " is-active" : " is-disabled"
            }`}
          >
            {value ? "Active" : "Disabled"}
          </span>
        ),
      },
    ],
    []
  );

  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: events,
    },
    useSortBy
  );

  const eventCountLabel = `${events.length.toLocaleString()} event${
    events.length === 1 ? "" : "s"
  }`;

  return (
    <>
      <div className="manager-program-settings__table-meta">
        <span className="manager-program-settings__table-count">
          {loading ? t("loading") : eventCountLabel}
        </span>

        {!loading && !errorMessage && events.length > 0 && (
          <span className="manager-program-settings__table-hint">
            Review event configuration, edit settings, or remove events from the
            current program.
          </span>
        )}
      </div>

      {loading && (
        <div className="manager-program-settings__empty">Loading events...</div>
      )}

      {!loading && errorMessage && (
        <div className="manager-program-settings__empty">{errorMessage}</div>
      )}

      {!loading && !errorMessage && events.length === 0 && (
        <div className="manager-program-settings__empty">
          No reward events are configured for this program.
        </div>
      )}

      {!loading && !errorMessage && events.length > 0 && (
        <div className="manager-program-settings__table-shell">
          <Table borderless className="manager-program-settings__table table--stack-mobile" {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      title={undefined}
                    >
                      <span data-tip={column.tooltip} style={{ display: "inline-block" }}>
                        {column.render("Header")}
                      </span>
                      {column.isSorted ? <Sorting column={column} /> : ""}
                    </th>
                  ))}
                  <CanEdit>
                    <th>Actions</th>
                  </CanEdit>
                </tr>
              ))}
            </thead>

            <tbody>
              {rows.map((row) => {
                prepareRow(row);
                const isBusy = busyEventId === row.original.id;

                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} data-label={typeof cell.column.Header === 'string' ? cell.column.Header : ''}>{cell.render("Cell")}</td>
                    ))}

                    <CanEdit>
                      <td data-label="Actions">
                        <div className="manager-program-settings__actions">
                          <button
                            type="button"
                            data-tip="Edit this event's settings"
                            className="manager-program-settings__action-btn"
                            onClick={() => onClickEditEvent(row.original.id)}
                            disabled={isBusy}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            data-tip="Permanently remove this event"
                            className="manager-program-settings__action-btn is-danger"
                            onClick={() => toggleDeleteHierarchy(row.original.id)}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </CanEdit>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <ModalWrapper
        name={modalName}
        isOpen={isOpen}
        setOpen={setOpen}
        toggle={toggle}
        event={event}
        setEvent={setEvent}
        setTrigger={setTrigger}
      />
      <RemoveHierarchyModal
        isDeleteHierarchy={isDeleteHierarchy}
        toggleDeleteHierarchy={toggleDeleteHierarchy}
        onDeleteHierarchy={onDeleteHierarchy}
        onDeleteEvent={onDeleteEvent}
        eventId={selectedEventId}
      />
      <RemoveSelectedHierarchyModal
        isDeleteHierarchyList={isDeleteHierarchyList}
        toggleDeleteHierarchyList={toggleDeleteHierarchyList}
        onDeleteHierarchyList={onDeleteHierarchyList}
        deleteHierarchyListData={deleteHierarchyListData}
        checkedState={checkedState}
        setCheckedState={setCheckedState}
        selectedEventId={selectedEventId}
      />
      <DeletedResultHierarchyModal
        isDeleteHierarchyResult={isDeleteHierarchyResult}
        toggleDeleteHierarchyResult={toggleDeleteHierarchyResult}
        deletehierarchyListDataResult={deleteHierarchyListDataResult}
      />
    </>
  );
};

export default Events;
